/**
 * Pantalla del tablero de **Pedidos de láminas** (`/herramientas/pedidos-laminas`).
 * Reemplaza el `PlaceholderPage` de `ROUTES.pedidosLaminas`. Equivale a
 * `pedidos_laminas.html` del SSR (demolido en esta feature).
 *
 * Replica el board del SSR sobre la API REST (`GET /pedidos-laminas`):
 *  - Tiras de estadística (por enviar / en curso / en camino).
 *  - 4 pestañas: Por enviar, En curso, En camino, "No hay en bodega" (faltantes).
 *  - Tarjetas de pedido con acciones por permiso/estado (`PedidoCard`).
 *  - Botón "Nuevo pedido" (modal de alta) con `add_pedidos_laminas`.
 *  - Enlace al Historial con `view_historial_pedidos_laminas`.
 *
 * Datos de servidor con TanStack Query (`usePedidosBoard`); el estado de UI (pestaña
 * activa, modal) es local. Exige `view_pedidos_laminas` vía
 * `RequirePedidosPermission`. Presentacional + orquestación; sin `fetch`.
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import { Button, Card, CardContent } from "@/components/ui";
import { useAuth } from "@/lib/auth";
import { ROUTES } from "@/features/navigation";

import {
  ADD_PERMISSION,
  VIEW_HISTORIAL_PERMISSION,
  VIEW_PERMISSION,
} from "../constants";
import { usePedidosBoard } from "../hooks/usePedidosBoard";
import type { PedidosBoard } from "../types";
import { FaltantesTab } from "./FaltantesTab";
import { PedidoCard } from "./PedidoCard";
import { PedidoFormModal } from "./PedidoFormModal";
import { RequirePedidosPermission } from "./RequirePedidosPermission";

type Tab = "por-enviar" | "en-curso" | "en-camino" | "faltantes";

function PedidosBoardView() {
  const { hasPermission } = useAuth();
  const { data, isPending, isError, error } = usePedidosBoard();
  const [tab, setTab] = useState<Tab>("por-enviar");
  const [modalOpen, setModalOpen] = useState(false);

  const canAdd = hasPermission(ADD_PERMISSION);
  const canViewHistorial = hasPermission(VIEW_HISTORIAL_PERMISSION);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pedidos de láminas</h1>
          <p className="text-sm text-slate-500">Gestión de pedidos entre sedes</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canViewHistorial && (
            <Button asChild variant="secondary" size="sm">
              <Link to={ROUTES.pedidosLaminasHistorial}>Historial</Link>
            </Button>
          )}
          {canAdd && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => setModalOpen(true)}
            >
              Nuevo pedido
            </Button>
          )}
        </div>
      </div>

      {isPending ? (
        <Card>
          <CardContent>
            <p className="py-8 text-center text-sm text-slate-500" role="status">
              Cargando pedidos…
            </p>
          </CardContent>
        </Card>
      ) : isError ? (
        <Card>
          <CardContent>
            <p className="py-8 text-center text-sm text-red-600" role="alert">
              {error instanceof Error && error.message
                ? error.message
                : "No se pudieron cargar los pedidos de láminas."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <BoardContent board={data} tab={tab} onTabChange={setTab} />
      )}

      {data && (
        <PedidoFormModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          origenSedeId={data.origen_sede_id}
          destinoSedeId={data.destino_sede_id}
          origenNombre={data.origen_sede_nombre}
          destinoNombre={data.destino_sede_nombre}
        />
      )}
    </div>
  );
}

interface BoardContentProps {
  board: PedidosBoard;
  tab: Tab;
  onTabChange: (tab: Tab) => void;
}

/** Tiras de estadística + pestañas + listas (contenido del board ya cargado). */
function BoardContent({ board, tab, onTabChange }: BoardContentProps) {
  const tabs: Array<{ id: Tab; label: string; count?: number }> = [
    { id: "por-enviar", label: "Por enviar", count: board.pendientes_envio.length },
    { id: "en-curso", label: "En curso", count: board.en_curso.length },
    { id: "en-camino", label: "En camino", count: board.en_camino.length },
    { id: "faltantes", label: "No hay en bodega" },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Tiras de estadística */}
      <div className="flex flex-wrap gap-3">
        <Stat label="por enviar" value={board.pendientes_envio.length} tone="warning" />
        <Stat label="en curso" value={board.en_curso.length} tone="info" />
        <Stat label="en camino" value={board.en_camino.length} tone="primary" />
      </div>

      {/* Pestañas */}
      <div
        className="flex flex-wrap gap-1 border-b-2 border-slate-200"
        role="tablist"
        aria-label="Estados de pedidos"
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`-mb-0.5 flex items-center gap-2 rounded-t-lg border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${
              tab === t.id
                ? "border-brand bg-blue-50 text-brand"
                : "border-transparent text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            }`}
            onClick={() => onTabChange(t.id)}
          >
            {t.label}
            {t.count !== undefined && (
              <span className="rounded-full bg-slate-200 px-2 text-xs text-slate-700">
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Contenido de la pestaña */}
      <div role="tabpanel">
        {tab === "por-enviar" && (
          <PedidoList
            pedidos={board.pendientes_envio}
            variant="por-enviar"
            emptyText="No hay pedidos pendientes por enviar"
          />
        )}
        {tab === "en-curso" && (
          <PedidoList
            pedidos={board.en_curso}
            variant="en-curso"
            emptyText="No hay pedidos en curso"
          />
        )}
        {tab === "en-camino" && (
          <PedidoList
            pedidos={board.en_camino}
            variant="en-camino"
            emptyText="No hay pedidos en camino"
            grid
          />
        )}
        {tab === "faltantes" && (
          <FaltantesTab
            faltantes={board.faltantes}
            origenSedeId={board.origen_sede_id}
            destinoSedeId={board.destino_sede_id}
            origenNombre={board.origen_sede_nombre}
            destinoNombre={board.destino_sede_nombre}
          />
        )}
      </div>
    </div>
  );
}

interface PedidoListProps {
  pedidos: PedidosBoard["pendientes_envio"];
  variant: "por-enviar" | "en-curso" | "en-camino";
  emptyText: string;
  /** En "en camino" se pintan en grilla (como el SSR). */
  grid?: boolean;
}

/** Lista de tarjetas de pedido (o un estado vacío). */
function PedidoList({ pedidos, variant, emptyText, grid = false }: PedidoListProps) {
  if (pedidos.length === 0) {
    return (
      <p className="py-12 text-center text-sm text-slate-400">{emptyText}</p>
    );
  }
  return (
    <div
      className={
        grid
          ? "grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
          : "flex flex-col gap-3"
      }
    >
      {pedidos.map((pedido) => (
        <PedidoCard key={pedido.id} pedido={pedido} variant={variant} />
      ))}
    </div>
  );
}

/** Tira de estadística (réplica de `.pl-stat`). */
function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "warning" | "info" | "primary";
}) {
  const tones: Record<typeof tone, string> = {
    warning: "border-amber-200 bg-amber-50 text-amber-800",
    info: "border-sky-200 bg-sky-50 text-sky-800",
    primary: "border-blue-200 bg-blue-50 text-blue-900",
  };
  return (
    <div
      className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold ${tones[tone]}`}
    >
      <strong className="text-lg font-extrabold">{value}</strong> {label}
    </div>
  );
}

/** Pantalla del tablero con el gating de permiso `view_pedidos_laminas`. */
export function PedidosLaminasScreen() {
  return (
    <RequirePedidosPermission
      permission={VIEW_PERMISSION}
      title="Pedidos de láminas"
      deniedMessage="No tienes permiso para ver los pedidos de láminas."
    >
      <PedidosBoardView />
    </RequirePedidosPermission>
  );
}
